/**
 * Body delle mutazioni su chiamate attive non coperte dai contratti
 * call-control discriminati (`call-control-contracts.ts`) né dall'originate
 * (`originate-call.ts`):
 *
 *   POST /api/v2/voip/active-calls/:call_uuid/hold
 *   POST /api/v2/voip/active-calls/:call_uuid/mute
 *   POST /api/v2/voip/active-calls/:call_uuid/record
 *   POST /api/v2/voip/active-calls/:call_uuid/attended-transfer
 *   POST /api/v2/voip/active-calls/:call_uuid/conference
 *
 * Tutti i body sono tolleranti ai campi extra (client storici) ma tipizzano
 * i campi interpretati. Il dominio SIP non fa parte del contratto: il backend
 * lo deriva dal tenant autenticato. I target che finiscono in un comando ESL
 * (`destination`, `thirdParty`) sono shape-checked qui al boundary con gli
 * stessi pattern degli helper brandizzati lato backend.
 */
import { z } from 'zod';
import { TransferDestinationSchema } from '../types/call-control-contracts';
import { ESL_DIAL_TARGET_PATTERN } from './originate-call';

/** `hold` assente o `true` = metti in attesa; `false` = riprendi. */
export const ActiveCallHoldBodySchema = z
  .object({ hold: z.boolean().optional() })
  .passthrough();
export type ActiveCallHoldBody = z.infer<typeof ActiveCallHoldBodySchema>;

/** `mute` assente o `true` = muta; `false` = riattiva l'audio. */
export const ActiveCallMuteBodySchema = z
  .object({ mute: z.boolean().optional() })
  .passthrough();
export type ActiveCallMuteBody = z.infer<typeof ActiveCallMuteBodySchema>;

/** `record` assente o `true` = avvia la registrazione; `false` = ferma. */
export const ActiveCallRecordBodySchema = z
  .object({ record: z.boolean().optional() })
  .passthrough();
export type ActiveCallRecordBody = z.infer<typeof ActiveCallRecordBodySchema>;

export const ATTENDED_TRANSFER_ACTIONS = ['start', 'complete', 'cancel'] as const;
export const AttendedTransferActionSchema = z.enum(ATTENDED_TRANSFER_ACTIONS);
export type AttendedTransferAction = z.infer<typeof AttendedTransferActionSchema>;

/**
 * Trasferimento assistito in tre fasi: `start` (richiede `destination`,
 * restituisce `consultUuid`), `complete` / `cancel` (richiedono `consultUuid`).
 */
export const ActiveCallAttendedTransferBodySchema = z
  .object({
    action: AttendedTransferActionSchema.default('start'),
    destination: TransferDestinationSchema.optional(),
    consultUuid: z.string().uuid().optional(),
  })
  .passthrough()
  .superRefine((body, ctx) => {
    if (body.action === 'start' && !body.destination) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['destination'],
        message: 'destination is required for start action',
      });
    }
    if ((body.action === 'complete' || body.action === 'cancel') && !body.consultUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['consultUuid'],
        message: 'consultUuid is required for this action',
      });
    }
  });
export type ActiveCallAttendedTransferBody = z.infer<typeof ActiveCallAttendedTransferBodySchema>;
export type ActiveCallAttendedTransferBodyInput = z.input<typeof ActiveCallAttendedTransferBodySchema>;

/** Conferenza a tre: `thirdParty` è il numero/interno da aggiungere. */
export const ActiveCallConferenceBodySchema = z
  .object({
    thirdParty: z.string().regex(ESL_DIAL_TARGET_PATTERN, 'Invalid thirdParty number'),
  })
  .passthrough();
export type ActiveCallConferenceBody = z.infer<typeof ActiveCallConferenceBodySchema>;
